/* eslint-disable import/prefer-default-export */

function currency(amount, roundToDollar) {
  if (Number.isNaN(amount) || amount === undefined || amount === null) {
    return '';
  }

  // In some cases we get a string in here which is an issue, but we need a quick fix, so
  // we'll just cast amount to a number before doing any kind of formatting
  // eslint-disable-next-line
    amount = +amount;
  const decimalPlaces = roundToDollar === true ? 0 : 2;
  let dollarFormat = amount
    .toFixed(decimalPlaces)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (dollarFormat.length > 0 && dollarFormat[0] === '-') {
    dollarFormat = dollarFormat.slice(1, dollarFormat.length);
  }
  dollarFormat = `$${dollarFormat}`;
  if (amount < 0) {
    dollarFormat = `-${dollarFormat}`;
  }

  return dollarFormat;
}

function commaSeparated(value) {
  if (Number.isNaN(value) || value === undefined || value === null) {
    return '';
  }

  const commaFormat = value
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return commaFormat;
}

function currencyFromCents(amount, roundToDollar) {
  return currency(amount / 100, roundToDollar);
}

function sentenceCase(phrase) {
  return phrase.charAt(0).toUpperCase() + phrase.substr(1);
}

function camelCase(phrase) {
  return phrase.replace(/\W+(.)/g, (match, chr) => chr.toUpperCase());
}

const properCase = str => str
  .toLowerCase()
  .split(' ')
  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ');

function toNormalCase(phrase) {
  return (
    phrase
      .replace(/([A-Z])/g, ' $1')
    // uppercase the first character
      .replace(/^./, str => str.toUpperCase())
  );
}

function formatPhoneNumber(phoneNumberString) {
  const cleaned = `${phoneNumberString}`.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }
  return '';
}

function percent(numberString) {
  return `${numberString}%`;
}

export {
  currency,
  currencyFromCents,
  sentenceCase,
  camelCase,
  toNormalCase,
  properCase,
  formatPhoneNumber,
  percent,
  commaSeparated,
};

// addSpacesBetweenWords inserts spaces between words in a camel-cased string
// "TestOneTwo" => "Test One Two"
export const addSpacesBetweenWords = (str) => {
  let string = str.replace(/([a-z])([A-Z])/g, '$1 $2');
  string = string.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
  return string;
};

export const capitalizeWord = str => str.charAt(0).toUpperCase() + str.slice(1);
export const capitalizeAllWords = str => str.replace(/(?:^|\s)\S/g, a => a.toUpperCase());
export const addSpacesAndCapitalize = str => capitalizeWord(addSpacesBetweenWords(str));
export const addSpacesAndCapitalizeAllWords = (str) => {
  if (typeof str !== 'string') {
    return '';
  }
  // Special Case to keep CD and HSA acronyms uppercase.
  let newStr = str.replace('cd', 'CD');
  newStr = newStr.replace('hsa', 'HSA');
  newStr = capitalizeAllWords(addSpacesBetweenWords(newStr));
  return newStr;
};

export const stripInvalidFileNameCharacters = str => str.replace(/['"]+/g, '');

export const sortByValue = objArray => objArray.sort((a, b) => (a.value > b.value ? 1 : -1));

export const formatRatio = (ratio, digits = 0) => {
  const divisor = 10 ** digits;

  return `${Math.round(Number(ratio) * 100 * divisor) / divisor}%`;
};

/**
 * @deprecated
 * Use `USDCurrencyCompactFormatter` below unless the output variation from this function is important
 */
export const truncateCurrency = (amount, precision = 0) => {
  let abbreviatedAmount;

  switch (Math.floor(Math.abs(amount)).toString().length) {
    case 4:
    case 5:
    case 6:
      abbreviatedAmount = `${Math.abs(amount / 1000).toFixed(precision)}K`;
      break;
    case 7:
    case 8:
    case 9:
      abbreviatedAmount = `${Math.abs(amount / 1000000).toFixed(precision)}M`;
      break;
    case 10:
    case 11:
    case 12:
      abbreviatedAmount = `${Math.abs(amount / 1000000000).toFixed(precision)}B`;
      break;
    case 13:
    case 14:
    case 15:
      abbreviatedAmount = `${Math.abs(amount / 1000000000).toFixed(precision)}T`;
      break;
    default:
      abbreviatedAmount = `${Math.abs(amount).toFixed(precision)}`;
      break;
  }

  return amount < 0 ? `-$${abbreviatedAmount}` : `$${abbreviatedAmount}`;
};

export const USDCurrencyCompactFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 2, minimumFractionDigits: 0,
});

export const USDCurrencyFullFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 2,
});

export const USDCurrencyFullFormatterNoFraction = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
});

export const PercentFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/**
 * numberToWord coverts integers between 0 and 999,999 to their word format. Ex 150 returns 'one hundred fifty'
 * @param num num must be a positive integer between 0 and 999,999
 * @return {string}
 */
export const numberToWord = (num) => {
  if (!Number.isInteger(num) || num > 999_999 || num < 0) {
    throw new Error(`numberToWord only supports positive integers up to 999,999, ${num} is an invalid value`);
  }
  let value = num;
  // Arrays for word mappings
  const digits = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
  const teens = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
  const thousands = ['', 'thousand'];

  // Handle Zero
  if (value === 0) return 'zero';

  // Convert to hundreds
  const convertHundreds = (n) => {
    let result = '';
    let number = n;
    if (number >= 100) {
      result += `${digits[Math.floor(number / 100)]} hundred `;
      number %= 100;
    }
    if (number >= 20) {
      result += `${tens[Math.floor(number / 10)]} `;
      number %= 10;
    }
    if (number >= 10 && number < 20) {
      result += `${teens[number - 10]} `;
      number = 0;
    }
    if (number > 0 && number < 10) {
      result += `${digits[number]} `;
    }
    return result.trim();
  };

  // Break number into thousands and hundreds
  let words = '';
  let scale = 0;
  while (value > 0) {
    const chunk = value % 1000;
    if (chunk > 0) {
      words = `${convertHundreds(chunk) + (thousands[scale] ? ` ${thousands[scale]}` : '')} ${words}`;
    }
    value = Math.floor(value / 1000);
    scale++;
  }
  return words.trim();
};
